// Last-visit marker — "when the author last saw Home", per device (pre-sync).
//
// The substrate for the workspace home's cold-return band ("sedan du var
// här", P45a). Like the theme, the display name, and the view state, this is
// DEVICE state, not a domain fact (memory project_theme_outside_event_log):
// it round-trips through localStorage and is never emitted as an event; when
// the sync substrate lands, a per-device last-seen marker stays local by
// design. Only the impure shell (AppShellHost) calls `readLastVisit`/
// `writeLastVisit`; the parse/serialize pair is pure.
//
// WORKSPACE-FLAT (flattened-object-model-design.md): with Project retired as
// a container there is exactly ONE landing surface (Home), so the marker
// collapses from a per-project map to a single ISO timestamp.
//
// Parse, don't validate (the viewState.ts pattern): localStorage hands back
// `string | null` written by ANY earlier build. Anything absent, malformed, or
// from a future shape (including an earlier build's per-project map) degrades
// to "never visited" — the band simply does not show — never a crash.

import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

// Namespaced key, matching the `theme.tsx` / `viewState.ts` convention.
const STORAGE_KEY = "bilda.lastVisit";

export type LastVisit = string | undefined;

// --- Parsing (pure) ----------------------------------------------------------

export const parseLastVisit = (raw: string | null): LastVisit => {
    if (raw === null) return undefined;
    let data: unknown;
    try {
        data = JSON.parse(raw);
    } catch {
        return undefined;
    }
    return typeof data === "string" ? data : undefined;
};

// --- Serializing (pure) ------------------------------------------------------

export const serializeLastVisit = (state: LastVisit): string =>
    JSON.stringify(state ?? null);

// --- The impure seam (host-only, like displayName.ts) ------------------------

const readLastVisit = (): LastVisit =>
    parseLastVisit(readDeviceKey(STORAGE_KEY));

const writeLastVisit = (state: LastVisit): void =>
    writeDeviceKey(STORAGE_KEY, serializeLastVisit(state));

// --- Capture-once (host-only) ------------------------------------------------

// The band's baseline for THIS page-load, memoised once. `captureVisit` reads
// the stored marker (the baseline the cold-return band compares against),
// then stamps "now" so the next return compares against this visit — but does
// BOTH exactly once per page-load. The read-then-write is not idempotent (a
// second run would read its own fresh stamp and the baseline would collapse
// to "now"), and neither a component ref nor a StrictMode guard survives a
// full remount — so the memo lives at module scope. The host calls this once
// on mount; repeat calls return the frozen baseline without touching storage.
let capturedBaseline: { readonly value: LastVisit } | undefined;

export const captureVisit = (): LastVisit => {
    if (capturedBaseline !== undefined) return capturedBaseline.value;
    const baseline = readLastVisit();
    capturedBaseline = { value: baseline };
    writeLastVisit(new Date().toISOString());
    return baseline;
};
